"use client";

import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowRightIcon } from "lucide-react";
import Link from "next/link";

import Loader from "@/components/Loader";
import { homeHeadlineClass } from "@/containers/home/homeTypography";
import { cn } from "@/lib/utils";
import {
  Card,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@gogive/design-system";

import "@/types/table-column-meta";

type HomeDashboardTableProps<TData> = {
  title: string;
  viewAllHref?: string;
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  isLoading?: boolean;
  emptyMessage: string;
  getRowId?: (originalRow: TData, index: number) => string;
};

export function HomeDashboardTable<TData>({
  title,
  viewAllHref,
  columns,
  data,
  isLoading,
  emptyMessage,
  getRowId,
}: HomeDashboardTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
  });

  const rows = table.getRowModel().rows;
  const showEmpty = !isLoading && rows.length === 0;
  const showLoader = Boolean(isLoading) && rows.length === 0;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-4 border-0 bg-card px-6">
        <CardTitle
          className={cn(homeHeadlineClass, "text-lg font-bold tracking-tight")}
        >
          {title}
        </CardTitle>
        {viewAllHref ? (
          <Link
            href={viewAllHref}
            className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
          >
            View all
            <ArrowRightIcon className="size-4" aria-hidden />
          </Link>
        ) : null}
      </CardHeader>
      <div className="overflow-x-auto">
        {showLoader ? (
          <div className="flex justify-center px-6 py-12">
            <Loader size={24} />
          </div>
        ) : (
          <>
            <Table bordered scrollMode="horizontal" className="min-w-full">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cell.column.columnDef.meta?.cellClassName}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {showEmpty ? (
              <div className="border-t px-4 py-10 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : null}
          </>
        )}
      </div>
    </Card>
  );
}
